/**
 * Data Overview Tab
 *
 * Shows aggregated stats, health status, and layer statistics
 */
import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CircleStackIcon,
  ChartBarIcon,
  FolderIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'
import { dataStore } from '@/stores/DataStore'
import { knowledgeBaseStore } from '@/stores/KnowledgeBaseStore'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'

const DataOverviewTab: React.FC = observer(() => {
  useEffect(() => {
    // Fetch data on mount
    dataStore.fetchHealth()
    dataStore.fetchLayers()
    knowledgeBaseStore.fetchCollections()
    knowledgeGraphStore.fetchGraphs()
    dataStore.fetchEpisodes()
    dataStore.fetchEntities()
  }, [])

  const { health, layers, isLoadingHealth } = dataStore

  const getHealthIcon = () => {
    if (!health) return <XCircleIcon className="w-6 h-6 text-gray-400" />
    switch (health.status) {
      case 'healthy':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />
      case 'degraded':
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />
      default:
        return <XCircleIcon className="w-6 h-6 text-red-500" />
    }
  }

  const getHealthBadgeVariant = (status: string): 'success' | 'warning' | 'error' | 'gray' => {
    switch (status) {
      case 'healthy':
        return 'success'
      case 'degraded':
        return 'warning'
      case 'unhealthy':
        return 'error'
      default:
        return 'gray'
    }
  }

  const getConnectionBadgeVariant = (connected: boolean): 'success' | 'error' => {
    return connected ? 'success' : 'error'
  }

  return (
    <div className="space-y-6">
      {/* Health Status */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
        <Card className="bg-gray-50">
          {isLoadingHealth ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : health ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {getHealthIcon()}
                <div>
                  <p className="text-sm font-medium text-gray-700">Overall Status</p>
                  <Badge variant={getHealthBadgeVariant(health.status)}>
                    {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-6">
                {/* Single NeuroGraph engine badge — replaced separate Memgraph
                    + Weaviate badges at the engine-only cutover. The
                    `health.memgraph`/`health.weaviate` fields both reflect
                    engine reachability now (kept for back-compat). */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">NeuroGraph engine:</span>
                  <Badge variant={getConnectionBadgeVariant(health.memgraph && health.weaviate)}>
                    {health.memgraph && health.weaviate ? 'Connected' : 'Disconnected'}
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Unable to fetch health status</p>
          )}
        </Card>
      </div>

      {/* Quick Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="p-3 bg-blue-500/15 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dataStore.totalGraphs}</p>
            <p className="text-sm text-gray-600">Knowledge Graphs</p>
          </Card>

          <Card className="text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="p-3 bg-green-500/15 rounded-lg">
                <FolderIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dataStore.totalCollections}</p>
            <p className="text-sm text-gray-600">Collections</p>
          </Card>

          <Card className="text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="p-3 bg-purple-500/15 rounded-lg">
                <CircleStackIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dataStore.totalEpisodes}</p>
            <p className="text-sm text-gray-600">Episodes (L1)</p>
          </Card>

          <Card className="text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="p-3 bg-orange-500/15 rounded-lg">
                <CubeIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{dataStore.totalEntities}</p>
            <p className="text-sm text-gray-600">Entities (L2)</p>
          </Card>
        </div>
      </div>

      {/* Layer Statistics */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Layer Statistics</h3>
        {layers.length === 0 ? (
          <Card className="text-center py-8">
            <CircleStackIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-500">No layer data available</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {layers.map((layer) => (
              <Card
                key={layer.key}
                className={
                  layer.key === 'L1_EPISODIC' || layer.key === 'L2_SEMANTIC'
                    ? 'border-primary-200 bg-primary-50'
                    : ''
                }
              >
                <div className="text-center">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {layer.name}
                  </h4>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{layer.node_count}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{layer.description}</p>
                  {layer.avg_confidence !== null && (
                    <p className="text-xs text-gray-400 mt-2">
                      Avg confidence: {(layer.avg_confidence * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Storage Backend */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Storage Backend</h3>
        <div className="grid grid-cols-1 gap-4">
          <Card>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500/15 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">NeuroGraph engine</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Rust + RocksDB + inline HNSW. Single substrate for graph,
                  vector, decay, and 6-layer knowledge memory. Reached via
                  the data_platform NeuralGraph API.
                </p>
                <div className="mt-2">
                  <Badge variant={health?.memgraph && health?.weaviate ? 'success' : 'error'}>
                    {health?.memgraph && health?.weaviate ? 'Connected' : 'Disconnected'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

DataOverviewTab.displayName = 'DataOverviewTab'

export default DataOverviewTab
